import { beSMSSendGetCodeSmsConfirm } from 'Api/beSMSSendApi'
import evalCondition from 'Helpers/evalCondition'
import { logAppError } from 'Helpers/logger'
import { regexAlias } from 'mailup-react-helpers'
import {
  TOAST_VARIANT_ERROR,
  TOAST_VARIANT_SUCCESS,
  toastShow,
  TRUSTED_SMS_INPUT_VARIANT_INVALID,
  TRUSTED_SMS_INPUT_VARIANT_NEUTRAL,
  TRUSTED_SMS_INPUT_VARIANT_VERIFICATION
} from 'mailup-web-components'
import {
  modalSmsSenderState,
  modalTrustedSmsSenderCodeRequestState
} from 'Pages/Workflow/recoil/state/modals'
import { pageState } from 'Pages/Workflow/recoil/state/page'
import { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import {
  atomFamily,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil'

import fetchStatus from './fetchStatus'
import mapStatusToVariant from './mapStatusToVariant'

const regexPhoneNumber = /^(\+?)([0-9]+)([ ]*)$/

export const trustedSmsSenderState = atomFamily({
  key: 'trustedSmsSenderState',
  default: {
    value: '',
    isError: false,
    isDirty: false,
    invalidRequired: false,
    invalidNumericNotAllowed: false,
    invalidFormat: false,
    variant: TRUSTED_SMS_INPUT_VARIANT_NEUTRAL,
    trustedSmsBoxCodeVisible: false
  }
})

export async function sendConfirmationCode(SenderNumber) {
  const result = await beSMSSendGetCodeSmsConfirm(SenderNumber)
  return result
}

export const useTrustedSmsSender = (
  itemKey,
  required = false,
  initialValue = ''
) => {
  const intl = useIntl()
  const timer = useRef(null)
  const aliasIcon = evalCondition('co:SMSALIAS')

  const [
    {
      value,
      variant,
      invalidRequired,
      invalidNumericNotAllowed,
      invalidFormat
    },
    setState
  ] = useRecoilState(trustedSmsSenderState(itemKey))

  const setModalTrustedSmsSenderCodeRequest = useSetRecoilState(
    modalTrustedSmsSenderCodeRequestState
  )
  const [{ alias }, setModalSmsSenderState] =
    useRecoilState(modalSmsSenderState)
  const resetSmsSenderState = useResetRecoilState(modalSmsSenderState)

  const setPageState = useSetRecoilState(pageState)

  //  on mount
  useEffect(() => {
    resetSmsSenderState()
    setState((prev) => ({ ...prev, value: initialValue, required }))
  }, [])

  useEffect(() => {
    if (alias) {
      setState((prev) => ({
        ...prev,
        value: alias,
        variant: TRUSTED_SMS_INPUT_VARIANT_NEUTRAL,
        invalidRequired: false,
        invalidNumericNotAllowed: false,
        invalidFormat: false,
        trustedSmsBoxCodeVisible: false
      }))
    }
  }, [alias])

  const handleChange = ({ target: { value } }) => {
    innerHandleChange(value)
  }

  const innerHandleChange = async (inputVal) => {
    if (!required && inputVal === '') {
      setState((prev) => ({
        ...prev,
        value: inputVal,
        variant: TRUSTED_SMS_INPUT_VARIANT_NEUTRAL,
        invalidRequired: false,
        invalidNumericNotAllowed: false,
        invalidFormat: false,
        trustedSmsBoxCodeVisible: false
      }))
      return
    }

    const validRequired = required ? !!inputVal : true
    const validNumber = inputVal && inputVal.match(regexPhoneNumber)
    const validAlias =
      inputVal &&
      inputVal.match(regexAlias) &&
      inputVal.length > 1 &&
      inputVal.length < 12
    const opzDisableSmsNumSenders = evalCondition('co:DISABLESMSNUMSENDERS')
    const opzSmsNumericSender = evalCondition('co:SMSNUMERICSENDER')
    if (validNumber) {
      // INPUT: Numeric number
      if (opzDisableSmsNumSenders) {
        setState((prev) => ({
          ...prev,
          value: inputVal,
          invalidRequired: false,
          invalidNumericNotAllowed: true,
          variant: TRUSTED_SMS_INPUT_VARIANT_INVALID
        }))
      } else {
        if (opzSmsNumericSender) {
          try {
            setState((prev) => ({
              ...prev,
              value: inputVal,
              invalidRequired: false,
              invalidNumericNotAllowed: false,
              invalidFormat: false
            }))

            const statusResult = await fetchStatus(inputVal)

            setState((prev) => ({
              ...prev,
              variant: mapStatusToVariant(statusResult),
              trustedSmsBoxCodeVisible:
                mapStatusToVariant(statusResult) ===
                TRUSTED_SMS_INPUT_VARIANT_VERIFICATION
            }))
          } catch (error) {
            clearTimeout(timer.current)

            logAppError({
              appError: error,
              Component: 'useTrustedSmsSender',
              Method: 'innerHandleChange'
            })

            setState((prev) => ({
              ...prev,
              isError: true,
              value: inputVal
            }))
          }
        }
      }
    } else {
      // INPUT: Alias text
      setState((prev) => ({
        ...prev,
        value: inputVal,
        variant:
          validRequired && validAlias
            ? TRUSTED_SMS_INPUT_VARIANT_NEUTRAL
            : TRUSTED_SMS_INPUT_VARIANT_INVALID,
        invalidRequired: !validRequired,
        invalidNumericNotAllowed: false,
        invalidFormat: !validAlias,
        trustedSmsBoxCodeVisible: false
      }))
    }
  }

  const handleAliasClick = async () => {
    setModalSmsSenderState((prev) => ({
      ...prev,
      show: true
    }))
  }

  const handleVerifyClick = async () => {
    setPageState((prev) => ({
      ...prev,
      loading: true
    }))
    try {
      const res = await sendConfirmationCode(value)

      if (res.Result === true) {
        setModalTrustedSmsSenderCodeRequest((prev) => ({
          ...prev,
          show: false
        }))
        toastShow(
          intl.formatMessage({
            id: 'Load.SmsSendingWizard.RequestVerificationSuccessToast'
          }),
          TOAST_VARIANT_SUCCESS,
          'TrustedSmsSender__Success'
        )

        setState((prev) => ({
          ...prev,
          trustedSmsBoxCodeVisible: true,
          variant: TRUSTED_SMS_INPUT_VARIANT_VERIFICATION
        }))
      } else {
        toastShow(
          intl.formatMessage({
            id: 'Load.SmsSendingWizard.RequestVerificationErrorToast'
          }),
          TOAST_VARIANT_ERROR,
          'TrustedSmsSender__Error'
        )
      }
    } catch (error) {
      toastShow(
        intl.formatMessage({
          id: 'Load.SmsSendingWizard.RequestVerificationErrorToast'
        }),
        TOAST_VARIANT_ERROR,
        'TrustedSmsSender__Error'
      )

      logAppError({
        appError: error,
        Component: 'useTrustedSmsSender',
        Method: 'handleVerifyClick'
      })
    }
    setPageState((prev) => ({
      ...prev,
      loading: false
    }))
  }

  const i18n = {
    invalid: invalidRequired
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : invalidNumericNotAllowed
        ? intl.formatMessage({
            id: 'Load.TrustedSmsSender.NumericNotAllowed'
          })
        : invalidFormat
          ? intl.formatMessage({
              id: 'Load.TrustedSmsSender.WrongFormat'
            })
          : undefined,
    unverified: intl.formatMessage({
      id: 'Load.TrustedSmsSender.NotConfirmed'
    }),
    verification: intl.formatMessage({
      id: 'Load.TrustedSmsSender.ToBeConfirmed'
    }),
    blocked: intl.formatMessage({ id: 'Load.TrustedSmsSender.Blocked' }),
    verifyLink: intl.formatMessage({
      id: 'Load.TrustedSmsSender.RequestVerification'
    })
  }

  return {
    value,
    onChange: handleChange,
    onAliasClick: handleAliasClick,
    onVerifyClick: handleVerifyClick,
    variant,
    i18n,
    aliasIcon
  }
}
